import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/layouts/AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Users, Loader2, Trash2, KeyRound, Search, Crown, Clapperboard, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

function RoleBadge({ role }) {
  const map = {
    super_admin: { label: "Super admin", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", Icon: Crown },
    tenant_admin: { label: "Studio admin", cls: "bg-violet-500/15 text-violet-300 border-violet-500/30", Icon: Clapperboard },
    subscriber: { label: "Subscriber", cls: "bg-white/5 text-white/60 border-white/15", Icon: UserIcon },
  };
  const { label, cls, Icon } = map[role] || map.subscriber;
  return (
    <Badge className={`rounded-none border gap-1.5 uppercase tracking-widest text-[10px] ${cls}`}>
      <Icon className="w-3 h-3" /> {label}
    </Badge>
  );
}

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [me, setMe] = useState(null);
  const [pwTarget, setPwTarget] = useState(null); // user obj for reset dialog
  const [newPw, setNewPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    try {
      const [u, meRes] = await Promise.all([
        api.get("/super-admin/users"),
        api.get("/auth/me").catch(() => ({ data: null })),
      ]);
      setRows(u.data);
      setMe(meRes.data?.user || meRes.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (!q) return true;
      return (u.email || "").toLowerCase().includes(q)
        || (u.full_name || "").toLowerCase().includes(q)
        || (u.tenant_name || "").toLowerCase().includes(q);
    });
  }, [rows, roleFilter, search]);

  const counts = useMemo(() => {
    const c = { all: rows.length, super_admin: 0, tenant_admin: 0, subscriber: 0 };
    rows.forEach((u) => { c[u.role] = (c[u.role] || 0) + 1; });
    return c;
  }, [rows]);

  const resetPassword = async () => {
    if (!pwTarget) return;
    if ((newPw || "").length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.patch(`/super-admin/users/${pwTarget.id}/password`, { password: newPw });
      toast.success(`Password updated for ${pwTarget.email}`);
      setPwTarget(null); setNewPw("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async (u) => {
    setDeletingId(u.id);
    try {
      await api.delete(`/super-admin/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      setRows((prev) => prev.filter((x) => x.id !== u.id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const tabs = [
    { key: "all", label: "All" },
    { key: "super_admin", label: "Super admins" },
    { key: "tenant_admin", label: "Studio admins" },
    { key: "subscriber", label: "Subscribers" },
  ];

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 border border-white/15 flex items-center justify-center text-amber-300">
            <Users className="w-4 h-4" />
          </div>
          <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="admin-users-title">Users</h1>
        </div>
        <p className="text-white/55 text-sm max-w-xl">
          Everyone who can sign in. Reset a password or remove an account — handy for clearing out demo/test logins.
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setRoleFilter(t.key)}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] border transition-colors ${roleFilter === t.key ? "border-white/40 text-white bg-white/5" : "border-white/10 text-white/50 hover:text-white"}`}
              data-testid={`admin-users-filter-${t.key}`}
            >
              {t.label} <span className="text-white/30 ml-1">{counts[t.key] ?? 0}</span>
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-64">
          <Search className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email / name / studio"
            className="pl-9 bg-transparent border-white/15 rounded-none focus-visible:border-white text-sm h-9"
            data-testid="admin-users-search"
          />
        </div>
      </div>

      <div className="bg-[#121212] border border-white/10" data-testid="admin-users-card">
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-white/40 text-sm uppercase tracking-widest">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <Users className="w-8 h-8 text-white/20 mx-auto mb-3" />
            <p className="text-white/50 text-sm">No users match.</p>
          </div>
        ) : (
          <div className="divide-y divide-white/8">
            <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-[10px] uppercase tracking-[0.25em] text-white/35">
              <div className="col-span-4">User</div>
              <div className="col-span-3">Role</div>
              <div className="col-span-3">Studio</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {filtered.map((u) => {
              const isSelf = me && u.id === me.id;
              return (
                <div key={u.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-5 py-4 items-center" data-testid={`user-row-${u.id}`}>
                  <div className="md:col-span-4 min-w-0">
                    <div className="text-sm text-white truncate">{u.email}{isSelf && <span className="text-white/35 ml-2 text-[10px] uppercase tracking-widest">you</span>}</div>
                    <div className="text-[11px] text-white/40 truncate">{u.full_name || "—"}</div>
                  </div>
                  <div className="md:col-span-3"><RoleBadge role={u.role} /></div>
                  <div className="md:col-span-3 min-w-0">
                    {u.tenant_name ? (
                      <div className="text-sm text-white/80 truncate">{u.tenant_name} <span className="text-white/35 font-mono text-[11px]">/s/{u.tenant_slug}</span></div>
                    ) : <span className="text-white/25 text-xs">—</span>}
                  </div>
                  <div className="md:col-span-2 flex items-center gap-1 md:justify-end">
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => { setPwTarget(u); setNewPw(""); }}
                      className="rounded-none text-white/60 hover:text-white uppercase text-[10px] tracking-[0.2em]"
                      data-testid={`user-reset-${u.id}`}
                    >
                      <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Password
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost" size="sm"
                          disabled={isSelf || deletingId === u.id}
                          className="rounded-none text-white/50 hover:text-red-400 disabled:opacity-30"
                          title={isSelf ? "You can't delete your own account" : "Delete user"}
                          data-testid={`user-delete-${u.id}`}
                        >
                          {deletingId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#0a0a0a] border border-white/15 rounded-none text-white">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="font-display">Delete {u.email}?</AlertDialogTitle>
                          <AlertDialogDescription className="text-white/60">
                            This permanently removes <span className="text-white">{u.email}</span>
                            {u.tenant_name ? <> ({u.tenant_name})</> : null} and their profiles, watchlist and viewing history. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-none bg-transparent border-white/20 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteUser(u)} className="rounded-none bg-red-500 text-white hover:bg-red-600" data-testid={`user-delete-confirm-${u.id}`}>
                            Delete user
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reset password dialog */}
      <Dialog open={!!pwTarget} onOpenChange={(o) => { if (!o) { setPwTarget(null); setNewPw(""); } }}>
        <DialogContent className="bg-[#0a0a0a] border border-white/15 rounded-none text-white">
          <DialogHeader>
            <DialogTitle className="font-display">Reset password</DialogTitle>
            <DialogDescription className="text-white/60">
              Set a new password for <span className="text-white">{pwTarget?.email}</span>. They'll use it on their next sign-in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50">New password</Label>
            <Input
              type="text"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder="At least 6 characters"
              className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono"
              data-testid="reset-password-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setPwTarget(null); setNewPw(""); }} className="rounded-none border border-white/15 uppercase text-[11px] tracking-[0.2em]">Cancel</Button>
            <Button onClick={resetPassword} disabled={saving} className="rounded-none bg-white text-black hover:bg-white/90 uppercase text-[11px] tracking-[0.2em]" data-testid="reset-password-confirm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
